import React, { Fragment, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";

import IconButton from "../button/IconButton";
import { isMacLike } from "../../utils/keyboardShortcuts";

/**
 * キーボードショートカット一覧ドロワー。
 * グローバルヘッダーの「?」ボタン（HeaderShortcutHelp）から開き、画面右端から
 * スライドインする。Escape / オーバーレイクリック / × ボタンで閉じる。
 *
 * ショートカットの実装箇所:
 * - ⌥1 / ⌥2          … GlobalShortcutListener（全ページ共通）
 * - ⌥[ / ⌥]（= ⌥, / ⌥.）… GlobalShortcutListener（プロジェクト内 / 単一プロジェクトのタスクボード。前 / 次のプロジェクトへ）
 * - ⌥3〜⌥9 / Esc     … useProjectShortcuts（プロジェクト画面内のみ）
 * - 再押下の循環切替   … dispatchShortcutAction を各ページが useShortcutAction で購読
 * - ⌥← / ⌥→          … ProjectTaskBoards（タスクボードのタブ切り替え）
 * - ⌃C / ⌃X（Win: Alt+C / Alt+X）/ []+Space … TaskCell（タスクボードのセル編集中のみ）
 * - プレビュー系       … Preview.tsx の handleGlobalKeyDown
 * - ⌘/Ctrl+Enter      … CommentField（コメント入力欄にフォーカス中のみ）
 */

interface ShortcutGuideDrawerProps {
  /** ドロワーを表示するか */
  open: boolean;
  /** 閉じる要求（オーバーレイ / × / Escape） */
  onClose: () => void;
}

/** 1 行分のショートカット定義。keys は複数の組み合わせを「/」区切りで表示する */
type ShortcutItem = {
  keys: string[][];
  description: string;
};

type ShortcutGroup = {
  title: string;
  items: ShortcutItem[];
};

/** OS に合わせた修飾キー表記でショートカット一覧を組み立てる */
const buildShortcutGroups = (): ShortcutGroup[] => {
  const opt = isMacLike ? "⌥" : "Alt";
  const shift = isMacLike ? "⇧" : "Shift";
  const mod = isMacLike ? "⌘" : "Ctrl";
  // セル編集中のチェックボックス系。macOS は ⌥+英字が特殊文字入力（ç 等）に
  // なるため ⌃（Control）、Windows は文字を入力しない Alt を使う。
  const cellMod = isMacLike ? "⌃" : "Alt";
  return [
    {
      title: "全体",
      items: [
        { keys: [[opt, "1"]], description: "タスクボード" },
        { keys: [[opt, "2"]], description: "プロジェクトリスト" },
        {
          keys: [
            [opt, "["],
            [opt, "]"],
          ],
          description:
            "前 / 次のプロジェクトへ（今の画面のまま・プロジェクト内 / タスクボード）",
        },
        {
          keys: [
            [opt, ","],
            [opt, "."],
          ],
          description: "同上（US 配列など、⌥[ / ⌥] が効かない環境の代替）",
        },
        {
          keys: [["←"], ["→"]],
          description:
            "ホイールのカード送り（プロジェクトリスト / ホーム進行中）",
        },
        {
          keys: [["Enter"]],
          description:
            "選択中のカードを開く（プロジェクトリスト / ホーム進行中）",
        },
        {
          keys: [[shift]],
          description:
            "カードをスタックして全体量を見る（押している間・プロジェクトリスト / ホーム）",
        },
      ],
    },
    {
      title: "タスクボード",
      items: [
        {
          keys: [
            [opt, "←"],
            [opt, "→"],
          ],
          description: "前 / 次のボード（タブ）へ切り替え",
        },
        {
          keys: [[cellMod, "C"]],
          description: "チェックボックスを付け外し（セル入力中・複数行選択可）",
        },
        {
          keys: [[cellMod, "X"]],
          description: "チェックの ON / OFF を切り替え（セル入力中）",
        },
        {
          keys: [["[]", "Space"]],
          description: "行頭の「[]」+スペースをチェックボックスに変換（セル入力中）",
        },
      ],
    },
    {
      title: "プロジェクト内",
      items: [
        { keys: [[opt, "3"]], description: "ホーム" },
        { keys: [[opt, "4"]], description: "トークルーム" },
        { keys: [[opt, "5"]], description: "DM" },
        { keys: [[opt, "6"]], description: "プロジェクトの設定" },
        { keys: [[opt, "7"]], description: "イメージボード ON / OFF" },
        { keys: [[opt, "8"]], description: "ノート" },
        { keys: [[opt, "9"]], description: "共有ライブラリ" },
        {
          keys: [["Esc"]],
          description: "プロジェクトリストへ（ホーム / トークルーム / DM / プロジェクトの設定）",
        },
      ],
    },
    {
      title: "プレビュー",
      items: [
        { keys: [["Space"], ["K"]], description: "再生 / 一時停止" },
        { keys: [["J"], ["L"]], description: "5秒戻す / 5秒送る" },
        { keys: [["↑"], ["↓"]], description: "バリエーション切替" },
        {
          keys: [
            [opt, "←"],
            [opt, "→"],
          ],
          description: "テイク切替",
        },
        { keys: [["←"], ["→"]], description: "1フレーム移動" },
        {
          keys: [
            [shift, "←"],
            [shift, "→"],
          ],
          description: "1秒移動",
        },
        { keys: [[shift, "M"]], description: "現在位置でコメント入力" },
        { keys: [["C"]], description: "コメントパネル開閉（閉じるとタイムライン）" },
        { keys: [["Esc"]], description: "閉じる" },
      ],
    },
    {
      title: "コメント入力欄",
      items: [
        {
          keys: [[mod, "Enter"]],
          description:
            "入力中のコメント / メッセージを送信（トークルーム・ショットコメント・DM）",
        },
        { keys: [["Enter"]], description: "改行" },
      ],
    },
  ];
};

/* フローティングオーディオトグル（z-index: 1100）より上に重ねる */
const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  z-index: 1200;
`;

const Panel = styled(motion.aside)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(360px, 100vw);
  display: flex;
  flex-direction: column;
  background: #fff;
  border-left: 1px solid var(--soremo-border);
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.08);
  z-index: 1201;
`;

const PanelHeader = styled.div.attrs({ className: "u-row-between" })`
  flex-shrink: 0;
  height: 64px;
  padding: 0 16px 0 24px;
  border-bottom: 1px solid var(--soremo-border);
`;

const PanelTitle = styled.h2.attrs({ className: "heading-16" })`
  margin: 0;
  color: #000;
`;

const PanelBody = styled.div.attrs({ className: "u-col u-gap24" })`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
`;

const GroupTitle = styled.h3.attrs({ className: "heading-13" })`
  margin: 0;
  color: var(--soremo-light-gray);
`;

/* 説明を左・キーチップを右端に振り分ける。親（.u-col）は align-items:
   flex-start のため、width: 100% を明示しないと内容幅に縮んでしまう */
const ShortcutRow = styled.div.attrs({ className: "u-row-between u-gap12" })`
  width: 100%;
  min-height: 36px;
  padding: 4px 0;
`;

const RowDescription = styled.span.attrs({ className: "bodytext-11" })`
  color: #000;
`;

const RowKeys = styled.div.attrs({ className: "u-row u-gap2" })`
  flex-shrink: 0;
`;

/* キーチップの書体は AXIS Round M（小サイズの M ウェイト utility が無いため直指定） */
const KEY_FONT_FAMILY = '"A+mfCv-AXISラウンド 50 M StdN", "M PLUS 1p", sans-serif';

/* キーチップ。個別調整が必要な最小 UI のためサイズ直指定を許容 */
const Key = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border: 1px solid var(--soremo-border);
  border-bottom-width: 2px;
  border-radius: 4px;
  background: var(--soremo-background);
  font-family: ${KEY_FONT_FAMILY};
  font-weight: normal;
  font-size: 11px;
  line-height: 1;
  color: #000;
`;

const KeySeparator = styled.span`
  margin: 0 4px;
  font-size: 11px;
  color: var(--soremo-light-gray);
`;

const ShortcutGuideDrawer: React.FC<ShortcutGuideDrawerProps> = ({
  open,
  onClose,
}) => {
  // Escape で閉じる。プレビューページ等の window keydown（バブリング）より先に
  // capture フェーズで受け取り、伝播を止めて既存の Esc 動作（プレビューを閉じる等）
  // と競合しないようにする。
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () =>
      document.removeEventListener("keydown", handleKeyDown, {
        capture: true,
      });
  }, [open, onClose]);

  // 表示中は背景スクロールをロック
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const groups = useMemo(buildShortcutGroups, []);

  return createPortal(
    <AnimatePresence>
      {open && (
        <Fragment key="shortcut-guide">
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <Panel
            initial={{ x: "105%" }}
            animate={{ x: 0 }}
            exit={{ x: "105%" }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            role="dialog"
            aria-modal="true"
            aria-label="キーボードショートカット"
          >
            <PanelHeader>
              <PanelTitle>ショートカット</PanelTitle>
              <IconButton icon="close" onClick={onClose} ariaLabel="閉じる" />
            </PanelHeader>
            <PanelBody>
              {groups.map((group) => (
                <section key={group.title} className="u-col u-gap4 u-w100">
                  <GroupTitle>{group.title}</GroupTitle>
                  <div className="u-col u-w100">
                    {group.items.map((item) => (
                      <ShortcutRow key={item.description}>
                        <RowDescription>{item.description}</RowDescription>
                        <RowKeys>
                          {item.keys.map((combo, comboIdx) => (
                            <Fragment key={comboIdx}>
                              {comboIdx > 0 && <KeySeparator>/</KeySeparator>}
                              {combo.map((key, keyIdx) => (
                                <Key key={keyIdx}>{key}</Key>
                              ))}
                            </Fragment>
                          ))}
                        </RowKeys>
                      </ShortcutRow>
                    ))}
                  </div>
                </section>
              ))}
            </PanelBody>
          </Panel>
        </Fragment>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default ShortcutGuideDrawer;
